import React, { useEffect, useState } from 'react';
import FileTree from './Components/FileTree/FileTree';
import styles from './FileExplorer.module.scss';
import { FileNode } from '../../DataRoom';
import { Button } from '../../../../common/_custom';
import { dataRoomDownloadIcon, downloadDarkGray, downloadIconDataRoomHovered, uploadIcon } from '../../../../assets/hostedassets';
import { isEmpty, isLenderType } from '../../../../utils/utils';
import EmptyBank from '../../../Dashboard/DashboardModules/BankDataTable/components/EmptyBank/EmptyBank';
import useFetchDataRoomFiles, {
    getSelectedFileNodes,
    buildDownloadStructure,
    getAllFileIds,
    getAllFolderIds,
    findMasterFolderForNode,
} from '../../useFetchDataRoomFiles';
import CategorySelector from '../../../../common/CategorySelector/CategorySelector';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import { USER_TYPE } from '../../../../utils/constants/user';
import BulkActionBar from '../BulkActionBar/BulkActionBar';
import { toast } from 'react-toastify';
import { Mixpanel } from '../../../../utils/mixpanel';
import { AICA_DATAROOM_BULK_ACTION_COMPLETED, AICA_DATAROOM_MAGIC_UPLOAD } from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { getSubdomain } from '../../../../utils/utils';
import { MAGIC_UPLOAD_TYPE } from '../../../../store/investor/constants';
import useMagicUploadLogic from '../../../../common/MagicUpload/useMagicUploadLogic';
import DownloadForOfflineIcon from '../../../../assets/Svg/DownloadForOfflineIcon';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';
import { useAddToReportsVisible } from '../../../../common/Reports/useAddToReportsVisible';

interface FileExplorerProps {
    data: FileNode[];
    activeFilecount: number;
    archivedFilecount: number;
    selectedCompany: any;
    exportData: Function;
    fetchFiles: Function;
    setShowDataRoomModal: (value: {
        showModal: boolean;
        docId?: number;
        currentDocType?: string;
        selectedDocIds?: number[];
        onMoveSuccess?: () => void;
    }) => void;
    newFilePresent?: boolean;
    // isRecurCompany: boolean;
}

const FileExplorer: React.FC<FileExplorerProps> = ({
    data,
    activeFilecount,
    archivedFilecount,
    selectedCompany,
    exportData,
    fetchFiles,
    setShowDataRoomModal,
    newFilePresent,
    // isRecurCompany,
}) => {
    const { downloadTheFolder, downloading, downloadingNew, handleDeleteFile, handleArchiveFileBulk, handleUnarchiveFileBulk } =
        useFetchDataRoomFiles(fetchFiles);
    const [searchParams, setSearchParams] = useSearchParams();
    const [fileViewType, setFileViewType] = useState('');
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [isBulkActionBarVisible, setIsBulkActionBarVisible] = useState(false);
    const user = useAppSelector((state) => state.user);
    const isLender = isLenderType();
    const isPartner = user?.userData?.accountType === USER_TYPE.PARTNER;
    const isCompany = user?.userData?.accountType === USER_TYPE.COMPANY;
    const isLenderOrPartner = isLender || isPartner;
    const isArchiveView = fileViewType === 'archived';
    const { dataRoomDownloadVisibility } = useFeatureVisibility({
        dataRoomDownloadVisibility: VISIBILITY_KEYS.DATA_ROOM.DOWNLOAD,
    });
    const addToReportsVisible = useAddToReportsVisible();

    const { changeDrawerContent } = useMagicUploadLogic();

    const VIEW_TYPE = [
        {
            id: 'active',
            label: 'Active',
            count: activeFilecount ?? null,
        },
        {
            id: 'archived',
            label: 'Archived',
            count: archivedFilecount ?? null,
        },
    ];
    useEffect(() => {
        const fileViewType = searchParams.get('fileViewType');
        if (!fileViewType) {
            const defaultViewType = 'active';
            setFileViewType(defaultViewType);
            searchParams.set('fileViewType', defaultViewType);
        } else {
            setFileViewType(fileViewType);
        }
        // Update query params
        updateQueryParams(searchParams, setSearchParams, {});
    }, [searchParams]);

    const changeSelectedCategory = (categoryId) => {
        setFileViewType(categoryId);
        updateQueryParams(searchParams, setSearchParams, { fileViewType: categoryId });
        setSelectedItems(new Set()); // Clear selection when switching tabs
        setSelectedFolders(new Set()); // Clear folder selection when switching tabs
        // Folder open/close states are reset automatically via component keys
    };

    // Manage bulk action bar animation visibility
    useEffect(() => {
        if (selectedItems.size > 0) {
            setIsBulkActionBarVisible(true);
        } else {
            setIsBulkActionBarVisible(false);
        }
    }, [selectedItems.size]);

    // Helper function to recursively collect all docIds from selected items
    const getAllDocIds = (items: FileNode[]): string[] => {
        let docIds: string[] = [];
        items.forEach((item) => {
            if (item.docId) {
                docIds.push(String(item.docId));
            }
            if (item.innerFiles && item.innerFiles.length > 0) {
                docIds = [...docIds, ...getAllDocIds(item.innerFiles)];
            }
        });
        return docIds;
    };

    // Handle select all (both files and folders)
    const handleSelectAll = () => {
        const allFileIds = getAllFileIds(data);
        const allFolderIds = getAllFolderIds(data);
        const allSelected = selectedItems.size === allFileIds.length && selectedFolders.size === allFolderIds.length;

        if (allSelected) {
            setSelectedItems(new Set());
            setSelectedFolders(new Set());
        } else {
            setSelectedItems(new Set(allFileIds));
            setSelectedFolders(new Set(allFolderIds));
        }
    };

    // Get all file IDs within a folder (recursively)
    const getAllFileIdsInFolder = (folder: FileNode): string[] => {
        let fileIds: string[] = [];
        if (folder.innerFiles) {
            folder.innerFiles.forEach((item) => {
                if (item.type === 'file' && item.docId) {
                    fileIds.push(String(item.docId));
                } else if (item.type === 'folder' && item.innerFiles) {
                    fileIds = [...fileIds, ...getAllFileIdsInFolder(item)];
                }
            });
        }
        return fileIds;
    };

    // Get all folder IDs within a folder (recursively)
    const getAllFolderIdsInFolder = (folder: FileNode): string[] => {
        let folderIds: string[] = [];
        if (folder.innerFiles) {
            folder.innerFiles.forEach((item) => {
                if (item.type === 'folder') {
                    folderIds.push(item.name);
                    folderIds = [...folderIds, ...getAllFolderIdsInFolder(item)];
                }
            });
        }
        return folderIds;
    };

    // Find node by ID
    const findNodeById = (nodes: FileNode[], id: string): FileNode | null => {
        for (const node of nodes) {
            const nodeId = node.docId ? String(node.docId) : node.name;
            if (nodeId === id) return node;
            if (node.innerFiles) {
                const found = findNodeById(node.innerFiles, id);
                if (found) return found;
            }
        }
        return null;
    };

    // Find all parent folder IDs of a given item
    const findParentFolders = (nodes: FileNode[], targetId: string, parents: string[] = []): string[] => {
        for (const node of nodes) {
            if (node.type === 'folder' && node.innerFiles) {
                const nodeId = node.name;
                const directChild = node.innerFiles.find((child) => {
                    const childId = child.docId ? String(child.docId) : child.name;
                    return childId === targetId;
                });

                if (directChild) {
                    return [...parents, nodeId];
                }

                const foundInNested = findParentFolders(node.innerFiles, targetId, [...parents, nodeId]);
                if (foundInNested.length > parents.length) {
                    return foundInNested;
                }
            }
        }
        return parents;
    };

    // Check if all files in folder are selected
    const areAllFilesInFolderSelected = (folder: FileNode, selected: Set<string>): boolean => {
        const fileIds = getAllFileIdsInFolder(folder);
        return fileIds.length > 0 && fileIds.every((id) => selected.has(id));
    };

    // Handle individual item selection with folder/file logic
    const handleItemSelect = (itemId: string) => {
        const node = findNodeById(data, itemId);
        if (!node) return;

        const newSelected = new Set(selectedItems);
        const newSelectedFolders = new Set(selectedFolders);

        if (node.type === 'folder') {
            const fileIds = getAllFileIdsInFolder(node);
            const folderIds = getAllFolderIdsInFolder(node);

            if (selectedFolders.has(itemId)) {
                // Deselect folder and all children
                fileIds.forEach((id) => newSelected.delete(id));
                folderIds.forEach((id) => newSelectedFolders.delete(id));
                newSelectedFolders.delete(itemId);

                // Deselect all parent folders
                const parentFolders = findParentFolders(data, itemId);
                parentFolders.forEach((parentId) => newSelectedFolders.delete(parentId));
            } else {
                // Select folder and all children
                fileIds.forEach((id) => newSelected.add(id));
                folderIds.forEach((id) => newSelectedFolders.add(id));
                newSelectedFolders.add(itemId);
            }
        } else {
            if (newSelected.has(itemId)) {
                // Deselect file
                newSelected.delete(itemId);

                // Deselect all parent folders
                const parentFolders = findParentFolders(data, itemId);
                parentFolders.forEach((parentId) => newSelectedFolders.delete(parentId));
            } else {
                // Select file
                newSelected.add(itemId);
            }
        }

        setSelectedItems(newSelected);
        setSelectedFolders(newSelectedFolders);
    };

    // Get count of only files (not folders)
    const getSelectedFileCount = (): number => {
        return selectedItems.size;
    };

    // Check if folder should be shown as selected (only if explicitly selected)
    const isFolderFullySelected = (folderId: string): boolean => {
        return selectedFolders.has(folderId);
    };

    // Bulk Actions
    const handleBulkArchive = async () => {
        setBulkActionLoading(true);
        try {
            const selectedNodes = getSelectedFileNodes(data, selectedItems);

            if (selectedNodes.length === 0) {
                toast.error('No files selected');
                return;
            }

            // Collect all files with their respective docTypes
            const filesWithDocType = selectedNodes
                .filter((node) => node.type === 'file' && node.docId)
                .map((node) => {
                    const fileId = String(node.docId!);
                    const masterFolder = findMasterFolderForNode(data, fileId);
                    return {
                        docId: node.docId!,
                        docType: masterFolder,
                    };
                });

            if (filesWithDocType.length === 0) {
                toast.error('No files selected');
                return;
            }

            // Make a single API call with all files
            const result = await handleArchiveFileBulk(filesWithDocType);

            if (result !== false) {
                // Track Mixpanel event
                Mixpanel.track(AICA_DATAROOM_BULK_ACTION_COMPLETED, {
                    action_type: 'archive',
                    item_count: filesWithDocType.length,
                    lead_id: selectedCompany?.assesseeOrgId,
                    user_id: user?.userData?.userId,
                });

                setSelectedItems(new Set());
                setSelectedFolders(new Set());
                fetchFiles();
            }
        } catch (error) {
            toast.error('Failed to archive items');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleBulkMove = () => {
        const selectedNodes = getSelectedFileNodes(data, selectedItems);
        if (selectedNodes.length === 0) {
            toast.error('No items selected');
            return;
        }

        // Get all selected doc IDs (only files)
        const selectedDocIds = selectedNodes.filter((node) => node.type === 'file' && node.docId).map((node) => node.docId as number);

        // Open move modal with first selected file's docId and all selected doc IDs
        const firstFile = selectedNodes.find((node) => node.type === 'file');
        if (firstFile?.docId && selectedDocIds.length > 0) {
            setShowDataRoomModal({
                showModal: true,
                docId: firstFile.docId,
                currentDocType: firstFile.name,
                selectedDocIds: selectedDocIds,
                onMoveSuccess: () => {
                    // Track Mixpanel event
                    Mixpanel.track(AICA_DATAROOM_BULK_ACTION_COMPLETED, {
                        action_type: 'move',
                        item_count: selectedDocIds.length,
                        lead_id: selectedCompany?.assesseeOrgId,
                        user_id: user?.userData?.userId,
                    });

                    // Clear selections after successful move
                    setSelectedItems(new Set());
                    setSelectedFolders(new Set());
                },
            });
        }
    };

    const handleBulkDownload = async () => {
        setBulkActionLoading(true);
        try {
            if (selectedItems.size === 0) {
                toast.error('No items selected');
                return;
            }

            // Build structured hierarchy for download (maintains folder structure)
            const structuredData = buildDownloadStructure(data, selectedItems);

            if (structuredData.length === 0) {
                toast.error('No items to download');
                return;
            }

            await downloadTheFolder(structuredData, selectedCompany?.orgName ?? 'Data', false);

            // Track Mixpanel event
            Mixpanel.track(AICA_DATAROOM_BULK_ACTION_COMPLETED, {
                action_type: 'download',
                item_count: selectedItems.size,
                lead_id: selectedCompany?.assesseeOrgId,
                user_id: user?.userData?.userId,
            });

            setSelectedItems(new Set());
            setSelectedFolders(new Set());
        } catch (error) {
            toast.error('Failed to download items');
        } finally {
            setBulkActionLoading(false);
        }
    };

    const handleBulkMoveToActive = async () => {
        setBulkActionLoading(true);
        try {
            const selectedNodes = getSelectedFileNodes(data, selectedItems);

            if (selectedNodes.length === 0) {
                toast.error('No files selected');
                return;
            }

            // Collect all files with their respective docTypes
            const filesWithDocType = selectedNodes
                .filter((node) => node.type === 'file' && node.docId)
                .map((node) => {
                    const fileId = String(node.docId!);
                    const masterFolder = findMasterFolderForNode(data, fileId);
                    return {
                        docId: node.docId!,
                        docType: masterFolder,
                    };
                });

            if (filesWithDocType.length === 0) {
                toast.error('No files selected');
                return;
            }

            // Make a single API call with all files
            const result = await handleUnarchiveFileBulk(filesWithDocType);

            if (result !== false) {
                // Track Mixpanel event
                Mixpanel.track(AICA_DATAROOM_BULK_ACTION_COMPLETED, {
                    action_type: 'move_to_active',
                    item_count: filesWithDocType.length,
                    lead_id: selectedCompany?.assesseeOrgId,
                    user_id: user?.userData?.userId,
                });

                setSelectedItems(new Set());
                setSelectedFolders(new Set());
                fetchFiles();
            }
        } catch (error) {
            toast.error('Failed to move items to active');
        } finally {
            setBulkActionLoading(false);
        }
    };

    return (
        <div className={styles.fileExplorer}>
            <header className={styles.header}>
                <div className={styles.leftSubHeader}>
                    <h1>Dataroom</h1>
                    {!isLender && (
                        <div className={styles.selectorView}>
                            <div className={styles.separator} />
                            <div className={styles.CategorySelector}>
                                <CategorySelector
                                    categoryOptions={VIEW_TYPE}
                                    selectedCategory={fileViewType}
                                    changeSelectedTab={changeSelectedCategory}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {isPartner && <></>}

                {!isLenderOrPartner && (
                    <Button
                        disabled={isEmpty(data) || !newFilePresent || selectedItems.size > 0 || dataRoomDownloadVisibility.disabled}
                        text={
                            <div className={`${styles.ButtonTextContainer}`}>
                                <img src={dataRoomDownloadIcon} alt={'Download'} />
                                Download New
                            </div>
                        }
                        style={{
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '10rem',
                            padding: '10px 16px',
                        }}
                        loading={downloadingNew}
                        loadingText={'Downloading'}
                        onClick={async () => {
                            await downloadTheFolder(data, selectedCompany?.orgName ?? 'Data', true);
                        }}
                        variant={'purple'}
                    />
                )}
            </header>
            <div className={styles.fileList}>
                <div className={isPartner ? styles.fileTreeHeaderLenderView : styles.fileTreeHeader}>
                    <div className={styles.fileTreeHeaderItem}>
                        {!isPartner && !isEmpty(data) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '0.5rem' }}>
                                <span style={{ width: '0.75rem', display: 'inline-block' }}></span>
                                <input
                                    type="checkbox"
                                    checked={
                                        selectedItems.size > 0 &&
                                        selectedFolders.size > 0 &&
                                        selectedItems.size === getAllFileIds(data).length &&
                                        selectedFolders.size === getAllFolderIds(data).length
                                    }
                                    onChange={handleSelectAll}
                                    className={styles.checkbox}
                                />
                            </div>
                        )}
                        Data
                    </div>
                    <div className={styles.fileTreeHeaderItem}>Status</div>
                    <div className={styles.fileTreeHeaderItem}>Data From</div>
                    <div className={styles.fileTreeHeaderItem}>Data Till</div>
                    <div className={styles.fileTreeHeaderItem} style={{ paddingLeft: '2px' }}>
                        Last Updated
                    </div>
                </div>
                <div className={styles.FileTreeContainer}>
                    {isEmpty(data) ? (
                        <EmptyBank
                            text={'No Data Available'}
                            subText={'Upload data to submit this case.'}
                            buttonComponent={
                                <Button
                                    disabled={false}
                                    text={
                                        <div className={styles.uploadButton}>
                                            <DownloadForOfflineIcon fill={'#ffffff'} width={16} height={16} />
                                            Upload Data
                                        </div>
                                    }
                                    loading={false}
                                    loadingText={'Uploading'}
                                    onClick={() => {
                                        changeDrawerContent('magicUpload');
                                        Mixpanel.track(AICA_DATAROOM_MAGIC_UPLOAD, {
                                            userRole: user?.userData?.accountType,
                                            pnoPlatform: getSubdomain(),
                                            pnoId: user?.userData?.investorOrgId,
                                            userId: user?.userData?.userId,
                                            currentUrl: window.location.href,
                                            accessedPlatform: user?.userData?.accountType,
                                            individualGlobal: MAGIC_UPLOAD_TYPE.INDIVIDUAL,
                                            organizationId: selectedCompany?.assesseeOrgId,
                                        });
                                    }}
                                    variant={'purple'}
                                />
                            }
                        />
                    ) : (
                        <div style={{ height: '100%' }}>
                            <FileTree
                                data={data}
                                index={0}
                                fetchFiles={fetchFiles}
                                setShowDataRoomModal={setShowDataRoomModal}
                                selectedItems={selectedItems}
                                onItemSelect={handleItemSelect}
                                showCheckboxes={!isPartner}
                                isFolderFullySelected={isFolderFullySelected}
                                fileViewType={fileViewType}
                                dataRoomDownloadVisibility={dataRoomDownloadVisibility}
                                addToReportsVisible={addToReportsVisible}
                                // isRecurCompany={isRecurCompany}
                            />
                        </div>
                    )}
                </div>
            </div>

            {selectedItems.size > 0 && (
                <BulkActionBar
                    dataRoomDownloadVisibility={dataRoomDownloadVisibility}
                    selectedCount={getSelectedFileCount()}
                    onArchive={handleBulkArchive}
                    onMove={handleBulkMove}
                    onDownload={handleBulkDownload}
                    onMoveToActive={handleBulkMoveToActive}
                    onClearSelection={() => {
                        setSelectedItems(new Set());
                        setSelectedFolders(new Set());
                    }}
                    isMoveDisabled={selectedFolders.size > 0}
                    showArchiveButton={!isLenderOrPartner}
                    showMoveButton={!isLenderOrPartner}
                    isArchived={isArchiveView}
                    loading={bulkActionLoading}
                    isVisible={isBulkActionBarVisible}
                />
            )}
        </div>
    );
};

export default FileExplorer;
